import { useMemo, type ReactNode } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { Currency, PortfolioDecompositionResponse } from "@richrich/contracts";
import { ChartFrame, type LegendItem } from "./ChartFrame";
import { paletteFor } from "../lib/palette";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { Stat, directionFromSign } from "../primitives/Stat";
import {
  decimalSign,
  decimalToNumber,
  formatCompactMoney,
  formatDateLong,
  formatDateShort,
  formatMoney,
  formatPercent,
} from "../lib/format";

export interface DecompositionPanelProps {
  data: PortfolioDecompositionResponse;
  theme?: Theme;
  height?: number;
  loading?: boolean;
  title?: ReactNode;
  className?: string;
}

interface DecompRow {
  t: number;
  /** Cost basis still present = max(0, min(invested, value)) — clamped ≥ 0. */
  capital: number;
  /** Market gain above cost (≥ 0). */
  gain: number;
  /** Market loss below cost, as a positive magnitude (≥ 0). */
  loss: number;
  /** Portfolio value = startValue + window flows + window P&L (the snapshot's total). */
  value: number;
  /** Net external flow since window start (signed). */
  deposits: number;
  /** Market P&L since window start (signed). */
  pnl: number;
  /** Time-weighted return ratio. */
  twr: number;
}

interface TooltipInjected {
  active?: boolean;
  payload?: Array<{ payload?: DecompRow }>;
  label?: number;
}

/**
 * Profit-vs-deposits decomposition (ARCHITECTURE §2/§8). The main chart is a
 * single value-axis stack:
 *   • **capital** (blue) = cost basis still present,
 *   • **market gain** (green) stacked above it → the stack top is the portfolio value,
 *   • **market loss** (red) stacked above capital → the stack top is the deposits line.
 * All three segments are non-negative, so the stack is unambiguous in profit and
 * loss alike (avoiding recharts' mixed-sign stacking pitfall). Time-weighted
 * return is a *ratio*, a different scale from KRW, so — honoring the one-axis
 * rule — it renders as its own synchronized strip below rather than a second
 * y-axis overlay.
 */
export function DecompositionPanel({
  data,
  theme,
  height = 260,
  loading = false,
  title = "Profit vs. deposits",
  className,
}: DecompositionPanelProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const palette = paletteFor(resolved);
  const currency = data.baseCurrency as Currency;

  const rows = useMemo<DecompRow[]>(
    () =>
      data.points.map((p) => {
        // Per-point cumExternal/cumMarketPnl are rebased to the window start
        // (attributePeriod: both begin at 0), so the actual portfolio value
        // at t is startValue + flows + P&L — NOT flows + P&L, which in a
        // no-deposit window would collapse to the market P&L alone.
        const start = decimalToNumber(data.startValue);
        const deposits = decimalToNumber(p.cumExternal);
        const pnl = decimalToNumber(p.cumMarketPnl);
        const value = start + deposits + pnl;
        // The "deposits line": capital present without market movement.
        const invested = start + deposits;
        // Clamped so every segment is ≥ 0 for ANY input: in a net-withdrawal
        // window (invested < 0) the capital band bottoms out at 0 and the
        // remaining value reads as market gain, instead of a negative bottom
        // segment breaking the stack. Ordinary windows are unchanged
        // (capital = invested or value, gain = pnl⁺, loss = pnl⁻).
        const capital = Math.max(0, Math.min(invested, value));
        return {
          t: p.t,
          deposits,
          pnl,
          value,
          twr: p.twr,
          capital,
          gain: Math.max(0, value - capital),
          loss: Math.max(0, invested - capital),
        };
      }),
    [data.points, data.startValue],
  );

  const capitalColor = palette.categorical[0]!;
  const gainColor = palette.good;
  const lossColor = palette.critical;
  const valueColor = palette.ink;
  const twrColor = palette.categorical[4]!;

  const legend: LegendItem[] = [
    { label: "Capital (deposits)", color: capitalColor },
    { label: "Market gain", color: gainColor },
    { label: "Market loss", color: lossColor },
    { label: "Value", color: valueColor, line: true },
  ];

  const pnlSign = decimalSign(data.marketPnl);
  const flowSign = decimalSign(data.externalFlow);
  const twrSign = data.twr > 0 ? 1 : data.twr < 0 ? -1 : 0;
  const empty = data.points.length === 0;

  const TwrTick = (v: number): string => formatPercent(v, 1);
  const MoneyTick = (v: number): string => formatCompactMoney(v, currency);

  return (
    <ChartFrame
      title={title}
      subtitle={`${formatDateLong(data.from)} – ${formatDateLong(data.to)}`}
      legend={legend}
      height={height}
      loading={loading}
      empty={empty}
      className={className}
    >
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="End value" value={formatMoney(data.endValue, currency)} />
        <Stat
          label="Net deposits"
          value={formatMoney(data.externalFlow, currency, { signed: true })}
          delta={{ text: "external flow", direction: directionFromSign(flowSign), hint: "" }}
        />
        <Stat
          label="Market P&L"
          value={formatMoney(data.marketPnl, currency, { signed: true })}
          delta={{
            text: formatMoney(data.marketPnl, currency, { signed: true }),
            direction: directionFromSign(pnlSign),
            hint: "",
          }}
        />
        <Stat
          label="TWR"
          value={formatPercent(data.twr, 2, true)}
          delta={{ text: "time-weighted", direction: directionFromSign(twrSign), hint: "" }}
        />
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid vertical={false} stroke={palette.grid} />
          <XAxis
            dataKey="t"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(value: number) => formatDateShort(value)}
            tick={{ fill: palette.muted, fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: palette.axis }}
            minTickGap={40}
          />
          <YAxis
            tickFormatter={MoneyTick}
            tick={{ fill: palette.muted, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip
            cursor={{ stroke: palette.muted, strokeDasharray: "3 3" }}
            content={<DecompTooltip currency={currency} />}
          />
          <Area
            type="monotone"
            dataKey="capital"
            stackId="v"
            stroke={capitalColor}
            strokeWidth={1}
            fill={capitalColor}
            fillOpacity={0.55}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="gain"
            stackId="v"
            stroke={gainColor}
            strokeWidth={1}
            fill={gainColor}
            fillOpacity={0.5}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="loss"
            stackId="v"
            stroke={lossColor}
            strokeWidth={1}
            fill={lossColor}
            fillOpacity={0.45}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={valueColor}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="mt-2">
        <div className="mb-1 text-xs font-medium text-muted">Time-weighted return</div>
        <ResponsiveContainer width="100%" height={72}>
          <LineChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid vertical={false} stroke={palette.grid} />
            <XAxis dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]} hide />
            <YAxis
              tickFormatter={TwrTick}
              tick={{ fill: palette.muted, fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={56}
            />
            <ReferenceLine y={0} stroke={palette.axis} />
            <Tooltip
              cursor={{ stroke: palette.muted, strokeDasharray: "3 3" }}
              content={<TwrTooltip />}
            />
            <Line
              type="monotone"
              dataKey="twr"
              stroke={twrColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}

function DecompTooltip({
  active,
  payload,
  label,
  currency,
}: TooltipInjected & { currency: Currency }): ReactNode {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const pnlSign = row.pnl > 0 ? 1 : row.pnl < 0 ? -1 : 0;
  return (
    <div className="rounded-lg border border-hairline bg-elevated px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-ink">
        {formatDateLong(typeof label === "number" ? label : row.t)}
      </div>
      <TooltipRow label="Value" value={formatMoney(String(Math.round(row.value)), currency)} />
      <TooltipRow
        label="Deposits"
        value={formatMoney(String(Math.round(row.deposits)), currency)}
      />
      <TooltipRow
        label="Market P&L"
        value={formatMoney(String(Math.round(row.pnl)), currency, { signed: true })}
        tone={pnlSign}
      />
      <TooltipRow label="TWR" value={formatPercent(row.twr, 2, true)} />
    </div>
  );
}

function TwrTooltip({ active, payload, label }: TooltipInjected): ReactNode {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-hairline bg-elevated px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-ink">
        {formatDateLong(typeof label === "number" ? label : row.t)}
      </div>
      <TooltipRow label="TWR" value={formatPercent(row.twr, 2, true)} />
    </div>
  );
}

function TooltipRow({
  label,
  value,
  tone,
}: {
  label: ReactNode;
  value: ReactNode;
  tone?: number;
}): ReactNode {
  const color =
    tone === undefined || tone === 0 ? "text-ink" : tone > 0 ? "text-good-text" : "text-critical";
  return (
    <div className="flex items-center justify-between gap-6">
      <span className="text-muted">{label}</span>
      <span className={`tabular-nums font-medium ${color}`}>{value}</span>
    </div>
  );
}
